import React, { useEffect, useState, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";

import * as api from "../../../api";
import RfidModal from "../../RfidModal/RfidModal";
import CustomerModal from "../../CustomerModal/CustomerModal";

import Select from "react-select";

import "./UserForm.sass";
import TierButton from "../../tierButton/tierButton";
import CashOrCreditModal from "../../CashOrCreditModal/CashOrCreditModal";
import Autocomplete from "../../AutocompleteList/Autocomplete";
import { Button, Modal } from "react-bootstrap";
import { addRecentOngroundSale, getRecentOngroundSales } from "../../../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHistory, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";

function formatLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatSlotDateLabel(dateStr) {
  if (!dateStr) return "";
  const parts = dateStr.split("-").map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some(Number.isNaN)) return dateStr;
  const dt = new Date(parts[0], parts[1] - 1, parts[2]);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function tierHasSlots(tier) {
  return !!(tier && (tier.has_slots ?? tier.hasSlots));
}

function tierSurplusCents(tier) {
  if (!tier) return 0;
  const v = tier.on_ground_price_surplus ?? tier.onGroundPriceSurplus;
  return v != null ? v : 0;
}

function partyAllowsMultiTickets(party) {
  if (!party || !Object.keys(party).length) return false;
  return !!(
    party.ongroundAllowMultipleTickets ?? party.onground_allow_multiple_tickets
  );
}

function partyMaxOngroundTickets(party) {
  if (!party || !Object.keys(party).length) return 10;
  const m =
    party.ongroundMaxTicketsPerSale ?? party.onground_max_tickets_per_sale;
  return m != null ? m : 10;
}

/** Integer in [1, max] for API / totals; `raw` is the visible input string. */
function normalizedOngroundQuantity(raw, party) {
  const max = partyMaxOngroundTickets(party);
  const s = String(raw ?? "").trim();
  if (s === "") return 1;
  const n = parseInt(s, 10);
  if (Number.isNaN(n)) return 1;
  return Math.min(Math.max(1, n), max);
}

function partyAllowsMultipleTiers(party) {
  if (!party || !Object.keys(party).length) return false;
  return !!(
    party.ongroundAllowMultipleTiers ?? party.onground_allow_multiple_tiers
  );
}

function slotKey(slot) {
  return slot.id ?? slot.pk;
}

function generateIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return (
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2) +
    Math.random().toString(36).slice(2)
  );
}

function formatHistoryPaymentLabel(choice) {
  const c = String(choice || "").toLowerCase();
  if (c === "foc") return "FOC";
  if (c === "card") return "CREDIT CARD";
  if (c === "cash") return "CASH";
  return c ? c.toUpperCase() : "—";
}

function formatHistoryTicketLine(
  selectedTiers,
  tierRecords,
  effectiveTicketQuantity,
  party
) {
  const keys = Object.keys(selectedTiers);
  if (keys.length === 0) return "—";
  const useQty =
    partyAllowsMultiTickets(party) && keys.length === 1
      ? effectiveTicketQuantity
      : 1;
  if (keys.length === 1) {
    const tr = tierRecords.find((t) => String(t.pk) === String(keys[0]));
    const name = (tr?.name || "Ticket").trim();
    return `${useQty}x ${name}`;
  }
  return keys
    .map((pk) => {
      const tr = tierRecords.find((t) => String(t.pk) === String(pk));
      return (tr?.name || "Ticket").trim();
    })
    .join(" + ");
}

function formatHistoryAmountLabel(totalCents, selectedPurchaseType) {
  if (selectedPurchaseType?.value === "foc") return "0 EGP";
  return `${(totalCents / 100).toFixed(0)} EGP`;
}

/** "15:00:00" | "15:00" → "3:00 pm" */
function formatTimeAmPm(timeStr) {
  const s = String(timeStr ?? "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return s;
  let h = parseInt(m[1], 10) % 24;
  const min = parseInt(m[2], 10);
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = h % 12 || 12;
  return `${h12}:${String(min).padStart(2, "0")} ${ampm}`;
}

function slotRowToOption(slot) {
  const sk = slotKey(slot);
  const cap = slot.capacity;
  const booked = slot.bookedCount ?? slot.booked_count;
  const full = cap != null && booked != null && booked >= cap;
  const start = formatTimeAmPm(slot.startTime ?? slot.start_time);
  const end = formatTimeAmPm(slot.endTime ?? slot.end_time);
  const occ =
    booked != null && cap != null ? ` (${booked}/${cap})` : "";
  return {
    value: sk,
    label: `${start} – ${end}${occ}`,
    isFull: full,
  };
}

/** Visible on light pages: contrast control + menu (react-select v4 `styles` API). */
const SLOT_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    backgroundColor: "#e8edf2",
    borderColor: state.isFocused ? "#3d5a80" : "#6c7a89",
    boxShadow: state.isFocused ? "0 0 0 1px #3d5a80" : "none",
    minHeight: 40,
    fontSize: "0.95rem",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#eef3f7",
    border: "1px solid #5a6c7d",
    boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: 4,
    paddingBottom: 4,
    maxHeight: 280,
  }),
  option: (base, state) => ({
    ...base,
    paddingTop: 8,
    paddingBottom: 8,
    backgroundColor: state.isSelected
      ? "#3d5a80"
      : state.isFocused
      ? "#d8e4ed"
      : "#eef3f7",
    color: state.isSelected ? "#fff" : "#1a2332",
    cursor: "pointer",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#1a2332",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#5a6570",
  }),
  input: (base) => ({
    ...base,
    color: "#1a2332",
  }),
};

export default function UserForm({
  onSuccess = () => {},
  /** If set (e.g. Access Control + Pass activation), history opens this customer there. */
  onOpenSaleInPassActivation = null,
  party = {},
  event,
  currentUser,
}) {
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    getValues,
    setValue,
    watch,
    clearErrors,
  } = useForm();
  const [backendErrors, setBackendErrors] = useState({});
  const [successMessageCountdown, setSuccessMessageCountdown] = useState(0);
  const [showRfidModal, setShowRfidModal] = useState(false);
  const [successCustomer, setSuccessCustomer] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [selectedTiers, setSelectedTiers] = useState({});
  const [tiersConflicts, setTiersConflicts] = useState({});
  const [tiersError, setTiersError] = useState("");
  const [purchaseTypes, setPurchaseTypes] = useState([]);
  const [selectedPurchaseType, setSelectedPurchaseType] = useState({});
  const [creationType, setCreationType] = useState("create");
  const [creationTypeError, setCreationTypeError] = useState("");
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [expandAutocomplete, setExpandAutocomplete] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [tierRecords, setTierRecords] = useState([]);
  const [slotsByTier, setSlotsByTier] = useState({});
  const [slotsLoading, setSlotsLoading] = useState({});
  const [slotsFetchError, setSlotsFetchError] = useState({});
  const [selectedSlotByTier, setSelectedSlotByTier] = useState({});
  /** YYYY-MM-DD for slot tiers (defaults to today). */
  const [slotDateStr, setSlotDateStr] = useState(() => formatLocalDate(new Date()));
  const [showSlotDateModal, setShowSlotDateModal] = useState(false);
  const [pendingSlotDateStr, setPendingSlotDateStr] = useState(() =>
    formatLocalDate(new Date())
  );
  const [appliedVoucherCode, setAppliedVoucherCode] = useState("");
  const [voucherDiscountCents, setVoucherDiscountCents] = useState(0);
  const [voucherMessage, setVoucherMessage] = useState("");
  const [voucherInput, setVoucherInput] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState("");
  /** True when the teller used "Auto Generate" for this submission (sent as isDummy). */
  const [isDummyCustomer, setIsDummyCustomer] = useState(false);
  /** String so the field can be cleared while typing; blur/submit use clamped int. */
  const [ticketQuantityInput, setTicketQuantityInput] = useState("1");
  /**
   * Guards against duplicate sales when the network is slow or the user
   * double-taps. Every POST to /onground-create/ also carries an idempotency
   * key so the backend can dedupe concurrent attempts.
   */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const idempotencyKeyRef = useRef(generateIdempotencyKey());

  /** Last few sales from this device (localStorage) — name, tickets, price, payment. */
  const [recentSaleHistory, setRecentSaleHistory] = useState(() =>
    getRecentOngroundSales()
  );
  const [showSaleHistoryModal, setShowSaleHistoryModal] = useState(false);

  /**
   * After sale, the pass-activation / ticket-pass UI opens only for
   * "Create And Assign RFID" (check-in + RFID). Not for "Create only" or
   * "Create and check-in" only.
   */
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationCustomer, setActivationCustomer] = useState(null);
  const [activationIsActivated, setActivationIsActivated] = useState(null);
  const [activationIsStaff, setActivationIsStaff] = useState(null);
  const [activationIsCheckedIn, setActivationIsCheckedIn] = useState(null);
  const [activationCustomerAccess, setActivationCustomerAccess] = useState(
    null
  );
  const [activationError, setActivationError] = useState("");

  const watchMobileField = watch("mobileNumber");

  const selectedTierCount = Object.keys(selectedTiers).length;
  const singleTierSelected = selectedTierCount === 1;
  const maxTicketsPerSale = partyMaxOngroundTickets(party);
  const normalizedTicketQuantity = useMemo(
    () => normalizedOngroundQuantity(ticketQuantityInput, party),
    [ticketQuantityInput, maxTicketsPerSale]
  );
  const effectiveTicketQuantity =
    partyAllowsMultiTickets(party) && singleTierSelected
      ? normalizedTicketQuantity
      : 1;

  const totalCents = useMemo(() => {
    if (selectedPurchaseType?.value === "foc") return 0;
    let total = 0;
    for (const tierPk of Object.keys(selectedTiers)) {
      const tr = tierRecords.find((t) => String(t.pk) === String(tierPk));
      if (!tr) continue;
      if (tierHasSlots(tr)) {
        const sid = selectedSlotByTier[tierPk];
        const rows = slotsByTier[tierPk] || [];
        const slot = rows.find((s) => slotKey(s) === sid);
        if (!slot) continue;
        total += (slot.price || 0) + tierSurplusCents(tr);
      } else {
        total += (tr.price || 0) + tierSurplusCents(tr);
      }
    }
    const gross = total * effectiveTicketQuantity;
    if (selectedPurchaseType?.value === "foc") return 0;
    return Math.max(0, gross - voucherDiscountCents);
  }, [
    selectedTiers,
    tierRecords,
    selectedSlotByTier,
    slotsByTier,
    selectedPurchaseType,
    effectiveTicketQuantity,
    voucherDiscountCents,
  ]);

  const hasSlottedTierSelected = useMemo(() => {
    return Object.keys(selectedTiers).some((tierPk) => {
      const tr = tierRecords.find((t) => String(t.pk) === String(tierPk));
      return tierHasSlots(tr);
    });
  }, [selectedTiers, tierRecords]);

  const clearAppliedVoucher = () => {
    setAppliedVoucherCode("");
    setVoucherDiscountCents(0);
    setVoucherMessage("");
    setVoucherError("");
  };

  const onSubmit = ({ email, firstName, lastName, gender, mobileNumber }) => {
    if (isSubmitting) return;
    let error = false;
    if (Object.keys(selectedTiers).length === 0) {
      setTiersError(
        partyAllowsMultipleTiers(party)
          ? "Please select at least one ticket tier"
          : "Please select one ticket tier"
      );
      error = true;
    } else {
      setTiersError("");
    }
    if (!error) {
      for (const tierPk of Object.keys(selectedTiers)) {
        const tr = tierRecords.find((t) => String(t.pk) === String(tierPk));
        if (tierHasSlots(tr) && !selectedSlotByTier[tierPk]) {
          setTiersError("Select a time slot for each tier that uses slots.");
          error = true;
          break;
        }
      }
    }
    if (!creationType) {
      setCreationTypeError("Please select creation flow");
      error = true;
    } else {
      setCreationTypeError("");
    }
    if (!error) {
      if (selectedPurchaseType?.value != "foc") {
        setShowMethodModal(true);
      } else {
        setPaymentMethod(selectedPurchaseType?.value);
        if (creationType === "assign_rfid") {
          setShowRfidModal(true);
        } else {
          submit(null, selectedPurchaseType?.value == "foc" ? "foc" : "");
        }
      }
    } else {
      return;
    }
  };

  const submit = (rfid = null, choice = "") => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    const { email, firstName, lastName, gender, mobileNumber } = getValues();
    let selectedArray = Object.keys(selectedTiers).map((k) =>
      parseInt(k, 10)
    );
    let slotId = null;
    for (const tierPk of Object.keys(selectedTiers)) {
      const tr = tierRecords.find((t) => String(t.pk) === String(tierPk));
      if (!tierHasSlots(tr)) continue;
      const sid = selectedSlotByTier[tierPk];
      if (sid != null && sid !== "") {
        slotId = sid;
        break;
      }
    }
    const idempotencyKey = idempotencyKeyRef.current;
    api.customers
      .onGroundCreate(
        email,
        firstName,
        lastName,
        gender,
        mobileNumber,
        rfid,
        selectedArray,
        creationType,
        choice,
        party.pk,
        slotId,
        isDummyCustomer,
        effectiveTicketQuantity,
        idempotencyKey,
        appliedVoucherCode || null
      )
      .then((response) => {
        onSuccess(response);
        setSuccessCustomer(response);
        setSelectedTiers({});
        setTiersConflicts({});
        setSelectedSlotByTier({});
        setSlotsByTier({});
        setSlotDateStr(formatLocalDate(new Date()));
        clearAppliedVoucher();
        setVoucherInput("");
        setIsDummyCustomer(false);
        setTicketQuantityInput("1");
        const justRanCreationType = creationType;
        setCreationType("create");
        reset();
        setSuccessMessageCountdown(5);
        setBackendErrors({});
        setErrorMessage(null);
        setPaymentMethod("");
        idempotencyKeyRef.current = generateIdempotencyKey();

        const fullName = [response.firstName, response.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() || "Guest";
        setRecentSaleHistory(
          addRecentOngroundSale({
            customerPk: response.pk,
            partyPk: party.pk,
            fullName,
            ticketLine: formatHistoryTicketLine(
              selectedTiers,
              tierRecords,
              effectiveTicketQuantity,
              party
            ),
            amountLabel: formatHistoryAmountLabel(
              totalCents,
              selectedPurchaseType
            ),
            amountCents: totalCents,
            paymentLabel: formatHistoryPaymentLabel(choice),
          })
        );

        // Pass activation / entry-access customer modal: only for "Create And
        // Assign RFID" + this party flow. Never for "Create only" or "Create
        // and check-in" (no RFID in that path).
        if (
          justRanCreationType === "assign_rfid" &&
          party?.flow === "check-in-and-assignment" &&
          response?.pk != null &&
          party?.pk != null
        ) {
          if (onOpenSaleInPassActivation) {
            onOpenSaleInPassActivation({
              customerPk: response.pk,
              partyPk: party.pk,
            });
          } else {
            openActivationModal(response.pk);
          }
        }
      })
      .catch((error) => {
        if (
          error.response &&
          error.response.status &&
          error.response.status === 400
        ) {
          setBackendErrors(error.response.data);
          idempotencyKeyRef.current = generateIdempotencyKey();
        } else if (error?.response?.status === 403) {
          setBackendErrors({ nonFieldErrors: [error.response.data.detail] });
          idempotencyKeyRef.current = generateIdempotencyKey();
        }
        const data = error?.response?.data;
        if (data) {
          const dataKeys = Object.keys(data);
          if (dataKeys.length) {
            const firstVal = data[dataKeys[0]];
            setErrorMessage(Array.isArray(firstVal) ? firstVal[0] : firstVal);
          }
        } else {
          setErrorMessage("Network error — please try again.");
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    if (successMessageCountdown > 0) {
      setTimeout(
        () => setSuccessMessageCountdown(successMessageCountdown - 1),
        1000
      );
    } else {
      setSuccessCustomer(null);
    }
  }, [successMessageCountdown]);

  useEffect(() => {
    if (!event) return;
    const loadTiers = async () => {
      let data = await api.events.staffTicketTiersByEvent(event);
      let available_tiers = data.results.filter((tier) => {
        return !tier.isSoldOut && !tier.is_sold_out;
      });
      setTierRecords(available_tiers);
      setTiers(
        available_tiers.map((tier) => {
          const n2 = tier.name_line_2 || tier.nameLine2 || "";
          const n3 = tier.name_line_3 || tier.nameLine3 || "";
          return {
            label: `${tier.name} ${n2} ${n3}`,
            value: tier.pk,
            conflicts: tier.conflictingTiers || tier.conflicting_tiers,
          };
        })
      );
      if (available_tiers?.length == 1) {
        handleTierSelect(
          available_tiers[0]?.pk,
          available_tiers[0]?.conflictingTiers ||
            available_tiers[0]?.conflicting_tiers
        );
      }
    };
    loadTiers();
  }, [event]);

  useEffect(() => {
    const eventPk = event?.pk ?? event;
    if (!eventPk) return;
    const selectedKeys = Object.keys(selectedTiers);
    setSlotsByTier((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (!selectedKeys.includes(k)) delete next[k];
      });
      return next;
    });
    selectedKeys.forEach((tierPk) => {
      const tr = tierRecords.find((t) => String(t.pk) === String(tierPk));
      if (!tierHasSlots(tr)) return;
      setSlotsLoading((prev) => ({ ...prev, [tierPk]: true }));
      setSlotsFetchError((prev) => {
        const next = { ...prev };
        delete next[tierPk];
        return next;
      });
      api.events
        .slotsByTierAndDate(eventPk, tierPk, slotDateStr)
        .then((rows) => {
          setSlotsByTier((prev) => ({ ...prev, [tierPk]: rows || [] }));
        })
        .catch(() => {
          setSlotsFetchError((prev) => ({
            ...prev,
            [tierPk]: "Could not load slots.",
          }));
        })
        .finally(() => {
          setSlotsLoading((prev) => ({ ...prev, [tierPk]: false }));
        });
    });
  }, [selectedTiers, tierRecords, event, slotDateStr]);

  useEffect(() => {
    setSelectedSlotByTier({});
    clearAppliedVoucher();
  }, [slotDateStr]);

  useEffect(() => {
    if (selectedPurchaseType?.value === "foc") {
      clearAppliedVoucher();
      setVoucherInput("");
    }
  }, [selectedPurchaseType?.value]);

  useEffect(() => {
    if (appliedVoucherCode) {
      clearAppliedVoucher();
    }
  }, [selectedTiers, selectedSlotByTier, effectiveTicketQuantity, slotDateStr]);

  useEffect(() => {
    if (watchMobileField?.length >= 11) {
      api.customers.list(watchMobileField, true).then((res) => {
        let list = res.results?.map((item) => {
          return {
            id: item.pk,
            label:
              item.email +
              " - " +
              item.firstName +
              " " +
              item.lastName +
              " - " +
              item?.mobileNumber,
            email: item.email,
            gender: item.gender,
            firstName: item.firstName,
            lastName: item.lastName,
            mobileNumber: item.mobileNumber,
          };
        });
        setCustomerList(list);
        if (list?.length > 0) {
          setExpandAutocomplete(true);
        }
      });
    } else {
      setCustomerList([]);
    }
  }, [watchMobileField]);

  useEffect(() => {
    if (!partyAllowsMultiTickets(party)) {
      setTicketQuantityInput("1");
    }
  }, [party]);

  useEffect(() => {
    if (!singleTierSelected) {
      setTicketQuantityInput("1");
    }
  }, [singleTierSelected]);

  useEffect(() => {
    if (currentUser?.hasCreateFocPermission) {
      setPurchaseTypes([
        { label: "Paid", value: "onground" },
        { label: "Free of charge", value: "foc" },
      ]);
    } else {
      setPurchaseTypes([{ label: "Paid", value: "onground" }]);
    }
    setSelectedPurchaseType({ label: "Paid", value: "onground" });
  }, [currentUser]);

  const handleTierSelect = (value, conflicts) => {
    const allowMultiTier = partyAllowsMultipleTiers(party);
    if (allowMultiTier) {
      let selected = { ...selectedTiers };
      let currentConflicts = { ...tiersConflicts };
      if (selected[value]) {
        delete selected[value];
        setSelectedSlotByTier((prev) => {
          const next = { ...prev };
          delete next[value];
          return next;
        });
        currentConflicts = {};
        for (const tier of Object.keys(selected)) {
          (selected[tier].conflicts || []).forEach((conflict) => {
            if (!currentConflicts[conflict]) {
              currentConflicts[conflict] = true;
            }
          });
        }
      } else {
        selected[value] = { id: value, conflicts };
        conflicts.forEach((conflict) => {
          if (!currentConflicts[conflict]) {
            currentConflicts[conflict] = true;
          }
        });
      }
      setTiersConflicts(currentConflicts);
      setSelectedTiers(selected);
      return;
    }
    if (selectedTiers[value]) {
      setSelectedTiers({});
      setSelectedSlotByTier({});
      setTiersConflicts({});
      return;
    }
    setSelectedSlotByTier({});
    const selected = {
      [value]: { id: value, conflicts },
    };
    const currentConflicts = {};
    conflicts.forEach((conflict) => {
      currentConflicts[conflict] = true;
    });
    setTiersConflicts(currentConflicts);
    setSelectedTiers(selected);
  };

  const handleCreationTypeChange = (value) => {
    setCreationType(value);
  };

  const handleMethodChoice = (choice) => {
    if (isSubmitting) return;
    setPaymentMethod(choice);
    setShowMethodModal(false);
    if (creationType === "assign_rfid") {
      setShowRfidModal(true);
    } else {
      submit(null, choice);
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setIsDummyCustomer(false);
    setExpandAutocomplete(false);
    if (getValues("mobileNumber")) {
      clearErrors("mobileNumber");
    }
    setValue("email", customer?.email, { shouldValidate: true });
    setValue("firstName", customer?.firstName, { shouldValidate: true });
    setValue("lastName", customer?.lastName, { shouldValidate: true });
    setValue("gender", customer?.gender, { shouldValidate: true });
  };

  const handleClearAutocomplete = (customer) => {
    setSelectedCustomer({});
    setIsDummyCustomer(false);
    setValue("mobileNumber", "", { shouldValidate: true });
    setValue("email", "", { shouldValidate: true });
    setValue("firstName", "", { shouldValidate: true });
    setValue("lastName", "", { shouldValidate: true });
    setValue("gender", "", { shouldValidate: true });
  };

  const openActivationModal = (customerPk) => {
    if (customerPk == null || party?.pk == null) return;
    setActivationError("");
    setActivationCustomer(null);
    setActivationIsActivated(null);
    setActivationIsStaff(null);
    setActivationIsCheckedIn(null);
    setActivationCustomerAccess(null);
    setShowActivationModal(true);
    api.entryAccess
      .entryAccessByPK(customerPk, party.pk)
      .then((data) => {
        const user = data.user || {};
        const staff = !!(
          user.isAccessControl ||
          user.isOutletManager ||
          user.isTeller ||
          user.isVendor ||
          user.isStaff ||
          user.isNcStaff
        );
        const loadedCustomer = data.customer || {};
        loadedCustomer.tickets = data.tickets;
        loadedCustomer.ticketPass = data.ticket_pass ?? data.ticketPass;
        setActivationCustomer(loadedCustomer);
        setActivationIsActivated(data.isActivated);
        setActivationIsStaff(staff);
        setActivationIsCheckedIn(data.isCheckedIn);
        setActivationCustomerAccess(staff ? true : data.hasAccess);
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setActivationError(detail || "Could not load customer for activation.");
      });
  };

  const closeActivationModal = () => {
    setShowActivationModal(false);
  };

  const openCustomerFromHistory = (sale) => {
    if (isSubmitting || !sale?.customerPk) return;
    setShowSaleHistoryModal(false);
    if (onOpenSaleInPassActivation) {
      onOpenSaleInPassActivation({
        customerPk: sale.customerPk,
        partyPk: sale.partyPk,
      });
    } else {
      openActivationModal(sale.customerPk);
    }
  };

  const buildVoucherValidatePayload = () => {
    const eventPk = event?.pk ?? event;
    const tierPks = Object.keys(selectedTiers).map((k) => parseInt(k, 10));
    let slotId = null;
    for (const tierPk of Object.keys(selectedTiers)) {
      const tr = tierRecords.find((t) => String(t.pk) === String(tierPk));
      if (!tierHasSlots(tr)) continue;
      const sid = selectedSlotByTier[tierPk];
      if (sid != null && sid !== "") {
        slotId = sid;
        break;
      }
    }
    return {
      eventId: eventPk,
      tiers: tierPks,
      slotId,
      ticketQuantity: effectiveTicketQuantity,
      purchaseType: selectedPurchaseType?.value || "onground",
      customerEmail: getValues("email") || "",
    };
  };

  const handleApplyVoucher = async (e) => {
    e?.preventDefault();
    const code = voucherInput.trim();
    if (!code) {
      setVoucherError("Enter a voucher code");
      return;
    }
    if (Object.keys(selectedTiers).length === 0) {
      setVoucherError("Select a ticket tier first");
      return;
    }
    if (selectedPurchaseType?.value === "foc") {
      setVoucherError("Vouchers do not apply to free-of-charge sales");
      return;
    }
    for (const tierPk of Object.keys(selectedTiers)) {
      const tr = tierRecords.find((t) => String(t.pk) === String(tierPk));
      if (tierHasSlots(tr) && !selectedSlotByTier[tierPk]) {
        setVoucherError("Select a time slot before applying a voucher");
        return;
      }
    }
    setVoucherLoading(true);
    setVoucherError("");
    try {
      const payload = buildVoucherValidatePayload();
      const data = await api.vouchers.validateOnground({
        code,
        ...payload,
      });
      if (!data?.valid) {
        throw new Error(data?.message || "Invalid voucher code");
      }
      setAppliedVoucherCode(data?.voucher?.code || code);
      setVoucherDiscountCents(data?.discountCents || 0);
      setVoucherMessage(data?.message || "");
      setVoucherInput("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.nonFieldErrors?.[0] ||
        err?.message ||
        "Invalid voucher code";
      setVoucherError(detail);
      clearAppliedVoucher();
    } finally {
      setVoucherLoading(false);
    }
  };

  const openSlotDateModal = () => {
    setPendingSlotDateStr(slotDateStr);
    setShowSlotDateModal(true);
  };

  const confirmSlotDate = () => {
    if (pendingSlotDateStr) {
      setSlotDateStr(pendingSlotDateStr);
    }
    setShowSlotDateModal(false);
  };

  const handleAutoGenerate = () => {
    api.customers
      .autoGenerate()
      .then(({ email, firstName, lastName, gender, mobileNumber }) => {
        setIsDummyCustomer(true);
        setValue("mobileNumber", mobileNumber, { shouldValidate: true });
        setValue("email", email, { shouldValidate: true });
        setValue("firstName", firstName, { shouldValidate: true });
        setValue("lastName", lastName, { shouldValidate: true });
        setValue("gender", gender, { shouldValidate: true });
      });
  };

  const ticketFieldErrors = [
    ...(tiersError ? [tiersError] : []),
    ...(backendErrors.ticketQuantity ?? backendErrors.ticket_quantity ?? []),
    ...(backendErrors.slotId ?? backendErrors.slot_id ?? []),
  ];

  return (
    <React.Fragment>
      {errorMessage ? (
        <div
          className="user-form-sale__banner user-form-sale__banner--error"
          role="alert"
        >
          {errorMessage}
        </div>
      ) : null}
      <div className="user-form-sale user-form-sale--layout">
        <div className="user-form-sale__main-wrap">
        <Row
          className="UserForm user-form-sale__row"
          onClick={() => setExpandAutocomplete(false)}
        >
          <Col xs={12}>
            <section
              className="user-form-sale__section user-form-sale__section--tickets"
              aria-labelledby="user-form-tickets-heading"
            >
              <h2
                id="user-form-tickets-heading"
                className="user-form-sale__section-title"
              >
                Tickets
              </h2>
              {ticketFieldErrors.length > 0 && (
                <ul
                  className="user-form-sale__error-list"
                  role="status"
                  aria-live="polite"
                >
                  {ticketFieldErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
              <div className="user-form-sale__tier-grid">
                {tiers.map((tier) => (
                  <div key={tier.value} className="user-form-sale__tier-cell">
                    <TierButton
                      label={tier.label}
                      value={tier.value}
                      onSelect={handleTierSelect}
                      status={
                        selectedTiers[tier.value]
                          ? "selected"
                          : tiersConflicts[tier.value]
                          ? "disabled"
                          : ""
                      }
                      conflicts={tier.conflicts}
                    />
                  </div>
                ))}
              </div>
              {partyAllowsMultiTickets(party) && singleTierSelected && (
                <div className="user-form-sale__qty-block">
                  <label
                    htmlFor="ticketQuantity"
                    className="user-form-sale__field-label"
                  >
                    Number of tickets
                  </label>
                  <div className="ticket-qty-stepper">
                    <Button
                      type="button"
                      variant="outline-primary"
                      className="ticket-qty-stepper__btn"
                      aria-label="Decrease ticket count"
                      disabled={normalizedTicketQuantity <= 1}
                      onClick={() =>
                        setTicketQuantityInput(
                          String(
                            Math.max(
                              1,
                              normalizedOngroundQuantity(
                                ticketQuantityInput,
                                party
                              ) - 1
                            )
                          )
                        )
                      }
                    >
                      -
                    </Button>
                    <input
                      id="ticketQuantity"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      className="form-control user-form-sale__input"
                      aria-valuemin={1}
                      aria-valuemax={maxTicketsPerSale}
                      aria-valuenow={normalizedTicketQuantity}
                      value={ticketQuantityInput}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        setTicketQuantityInput(digits);
                      }}
                      onBlur={() => {
                        setTicketQuantityInput(
                          String(
                            normalizedOngroundQuantity(
                              ticketQuantityInput,
                              party
                            )
                          )
                        );
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline-primary"
                      className="ticket-qty-stepper__btn"
                      aria-label="Increase ticket count"
                      disabled={normalizedTicketQuantity >= maxTicketsPerSale}
                      onClick={() =>
                        setTicketQuantityInput(
                          String(
                            Math.min(
                              maxTicketsPerSale,
                              normalizedOngroundQuantity(
                                ticketQuantityInput,
                                party
                              ) + 1
                            )
                          )
                        )
                      }
                    >
                      +
                    </Button>
                  </div>
                 
                </div>
              )}
              {partyAllowsMultipleTiers(party) && (
                <p className="user-form-sale__hint user-form-sale__hint--multi">
                  Multi-tier: combine passes in one sale. Quantity only when a single
                  tier is selected.
                </p>
              )}
              {hasSlottedTierSelected && (
                <div className="user-form-sale__slot-date-row">
                  <span className="user-form-sale__field-label">
                    Visit date: {formatSlotDateLabel(slotDateStr)}
                  </span>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    className="user-form-sale__slot-date-btn"
                    onClick={openSlotDateModal}
                    title="Choose another date"
                    aria-label="Choose visit date"
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </Button>
                </div>
              )}
              {Object.keys(selectedTiers).map((tierPk) => {
                const tr = tierRecords.find(
                  (t) => String(t.pk) === String(tierPk)
                );
                if (!tierHasSlots(tr)) return null;
                const rows = slotsByTier[tierPk] || [];
                const loading = slotsLoading[tierPk];
                const err = slotsFetchError[tierPk];
                const n2 = (tr.name_line_2 || tr.nameLine2 || "").trim();
                const tierTitle = [tr.name, n2].filter(Boolean).join("-");
                const slotOptions = rows.map(slotRowToOption);
                const selectedSlotValue =
                  slotOptions.find(
                    (o) => o.value === selectedSlotByTier[tierPk]
                  ) || null;
                const dateLabel = formatSlotDateLabel(slotDateStr);
                return (
                  <div
                    key={`slots-${tierPk}`}
                    className="user-form-sale__slot-field slot-tier-field"
                  >
                    <label className="user-form-sale__field-label slot-tier-field__label d-block">
                      Time slot — {tierTitle} ({dateLabel})
                    </label>
                    {loading && (
                      <span className="text-muted">Loading slots…</span>
                    )}
                    {err && <span className="error d-block">{err}</span>}
                    {!loading && !err && rows.length === 0 && (
                      <span className="text-muted">
                        No slots for {dateLabel}.
                      </span>
                    )}
                    {!loading && !err && rows.length > 0 && (
                      <Select
                        className="slot-tier-select"
                        classNamePrefix="slot-tier-select"
                        placeholder="Choose a time slot…"
                        isSearchable={rows.length > 10}
                        options={slotOptions}
                        value={selectedSlotValue}
                        onChange={(opt) =>
                          setSelectedSlotByTier((prev) => {
                            const next = { ...prev };
                            if (opt) next[tierPk] = opt.value;
                            else delete next[tierPk];
                            return next;
                          })
                        }
                        styles={SLOT_SELECT_STYLES}
                        formatOptionLabel={(option) => (
                          <span
                            className={
                              option.isFull
                                ? "slot-tier-select__option--full"
                                : ""
                            }
                          >
                            {option.label}
                          </span>
                        )}
                        isClearable
                        isDisabled={loading}
                      />
                    )}
                  </div>
                );
              })}
            </section>
          </Col>
          <Col xs={12}>
            {selectedPurchaseType?.value !== "foc" && (
              <div className="user-form-sale__voucher-block">
                <label
                  htmlFor="ongroundVoucherCode"
                  className="user-form-sale__field-label"
                >
                  Voucher
                </label>
                <form
                  className="user-form-sale__voucher-form"
                  onSubmit={handleApplyVoucher}
                >
                  <input
                    id="ongroundVoucherCode"
                    type="text"
                    className="form-control user-form-sale__input user-form-sale__voucher-input"
                    placeholder="Voucher code"
                    value={appliedVoucherCode || voucherInput}
                    onChange={(e) => setVoucherInput(e.target.value)}
                    disabled={voucherLoading || !!appliedVoucherCode}
                    autoComplete="off"
                  />
                  {appliedVoucherCode ? (
                    <Button
                      type="button"
                      variant="outline-secondary"
                      className="user-form-sale__voucher-action"
                      onClick={() => {
                        clearAppliedVoucher();
                        setVoucherInput("");
                      }}
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="primary"
                      className="user-form-sale__voucher-action"
                      disabled={voucherLoading || !voucherInput.trim()}
                    >
                      {voucherLoading ? "Applying…" : "Apply"}
                    </Button>
                  )}
                </form>
                {voucherError && (
                  <p className="user-form-sale__voucher-msg error mb-0">
                    {voucherError}
                  </p>
                )}
                {appliedVoucherCode && voucherMessage && !voucherError && (
                  <p className="user-form-sale__voucher-msg text-success mb-0">
                    {voucherMessage}
                  </p>
                )}
              </div>
            )}
            <div className="user-form-sale__total-bar" aria-live="polite">
              <span className="user-form-sale__total-label">Total</span>
              <span className="user-form-sale__total-value">
                {selectedPurchaseType?.value === "foc"
                  ? "0.00"
                  : (totalCents / 100).toFixed(2)}{" "}
                <span className="user-form-sale__total-currency">EGP</span>
              </span>
              {voucherDiscountCents > 0 &&
                selectedPurchaseType?.value !== "foc" && (
                  <span className="user-form-sale__total-discount">
                    Includes {(voucherDiscountCents / 100).toFixed(2)} EGP
                    voucher discount
                  </span>
                )}
            </div>
          </Col>
          <Col xs={12}>
            <section
              className="user-form-sale__section user-form-sale__section--pay"
              aria-labelledby="user-form-pay-heading"
            >
              <h2
                id="user-form-pay-heading"
                className="user-form-sale__section-title"
              >
                Payment &amp; what happens next
              </h2>
              {creationTypeError && (
                <p className="user-form-sale__creation-error error mb-1">
                  {creationTypeError}
                </p>
              )}
              <div className="user-form-sale__pay-grid">
                <div className="user-form-sale__pay-type">
                  <Select
                    aria-label="Payment type"
                    isSearchable={false}
                    options={purchaseTypes}
                    value={selectedPurchaseType}
                    onChange={(selected) => setSelectedPurchaseType(selected)}
                    className="customer-payment-type"
                    classNamePrefix="user-form-pay-select"
                  />
                </div>
                <div
                  className="user-form-sale__creation-choices"
                  role="radiogroup"
                  aria-label="After creating the customer"
                >
                  <label
                    className="user-form-sale__choice-label"
                    htmlFor="create"
                  >
                    <input
                      type="radio"
                      value="create"
                      id="create"
                      name="creation-type"
                      checked={creationType === "create"}
                      onChange={(e) => handleCreationTypeChange(e.target.value)}
                    />
                    <span className="user-form-sale__choice-line">
                      Create Only
                    </span>
                  </label>
                  <label
                    className="user-form-sale__choice-label"
                    htmlFor="check_in"
                  >
                    <input
                      type="radio"
                      value="check_in"
                      id="check_in"
                      name="creation-type"
                      checked={creationType === "check_in"}
                      onChange={(e) => handleCreationTypeChange(e.target.value)}
                    />
                    <span className="user-form-sale__choice-line">
                      Create And Check-in
                    </span>
                  </label>
                  <label
                    className="user-form-sale__choice-label"
                    htmlFor="assign_rfid"
                  >
                    <input
                      type="radio"
                      value="assign_rfid"
                      id="assign_rfid"
                      name="creation-type"
                      checked={creationType === "assign_rfid"}
                      onChange={(e) => handleCreationTypeChange(e.target.value)}
                    />
                    <span className="user-form-sale__choice-line">
                      Create And Assign RFID
                    </span>
                  </label>
                </div>
              </div>
            </section>
          </Col>
        <Col xs={12}>
          <section
            className="user-form-sale__section user-form-sale__section--guest"
            aria-labelledby="user-form-guest-heading"
          >
            <h2
              id="user-form-guest-heading"
              className="user-form-sale__section-title"
            >
              Lead guest
            </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="user-form-sale__form">
            {successMessageCountdown > 0 && (
              <Row className="mb-2">
                <Col xs={12}>
                  <span className="d-block text-center text-success small">
                    Customer created successfully
                  </span>
                </Col>
              </Row>
            )}

            <Row className="user-form-sale__guest-grid">
              <Col xs={12} md={6} className="mb-1 mb-md-2">
                <label className="user-form-sale__field-label" htmlFor="email">
                  Email
                </label>
                {errors.email && (
                  <span className="user-form-sale__field-error error d-block">
                    Email is required
                  </span>
                )}
                {backendErrors.email?.map((error, idx) => (
                  <span
                    key={idx}
                    className="user-form-sale__field-error error d-block"
                  >
                    {error}
                  </span>
                ))}
                <input
                  {...register("email", { required: true })}
                  className="form-control user-form-sale__input"
                  type="email"
                  name="email"
                  id="email"
                  autoComplete="email"
                />
              </Col>

              <Col xs={12} md={6} className="mb-1 mb-md-2">
                <label
                  className="user-form-sale__field-label"
                  htmlFor="firstName"
                >
                  First name
                </label>
                {errors.firstName && (
                  <span className="user-form-sale__field-error error d-block">
                    First name is required
                  </span>
                )}
                {backendErrors.firstName?.map((error, idx) => (
                  <span
                    key={idx}
                    className="user-form-sale__field-error error d-block"
                  >
                    {error}
                  </span>
                ))}
                <input
                  {...register("firstName", { required: true })}
                  className="form-control user-form-sale__input"
                  type="text"
                  name="firstName"
                  id="firstName"
                  autoComplete="given-name"
                />
              </Col>

              <Col xs={12} md={6} className="mb-1 mb-md-2">
                <label
                  className="user-form-sale__field-label"
                  htmlFor="lastName"
                >
                  Last name
                </label>
                {errors.lastName && (
                  <span className="user-form-sale__field-error error d-block">
                    Last name is required
                  </span>
                )}
                {backendErrors.lastName?.map((error, idx) => (
                  <span
                    key={idx}
                    className="user-form-sale__field-error error d-block"
                  >
                    {error}
                  </span>
                ))}
                <input
                  {...register("lastName", { required: true })}
                  className="form-control user-form-sale__input"
                  type="text"
                  name="lastName"
                  id="lastName"
                  autoComplete="family-name"
                />
              </Col>

              <Col xs={12} md={6} className="mb-1 mb-md-2">
                <span className="user-form-sale__field-label d-block">
                  Gender
                </span>
                {errors.gender && (
                  <span className="user-form-sale__field-error error d-block">
                    Gender is required
                  </span>
                )}
                {backendErrors.gender?.map((error, idx) => (
                  <span
                    key={idx}
                    className="user-form-sale__field-error error d-block"
                  >
                    {error}
                  </span>
                ))}
                <div
                  className="user-form-sale__gender-row"
                  role="radiogroup"
                  aria-label="Gender"
                >
                  <label
                    className="user-form-sale__choice-label user-form-sale__choice-label--inline"
                    htmlFor="male"
                  >
                    <input
                      {...register("gender")}
                      type="radio"
                      value="m"
                      id="male"
                      name="gender"
                    />
                    <span>Male</span>
                  </label>
                  <label
                    className="user-form-sale__choice-label user-form-sale__choice-label--inline"
                    htmlFor="female"
                  >
                    <input
                      {...register("gender")}
                      type="radio"
                      value="f"
                      id="female"
                      name="gender"
                    />
                    <span>Female</span>
                  </label>
                </div>
              </Col>

              <Col xs={12} md={6} className="mb-1 mb-md-2">
                <label
                  className="user-form-sale__field-label"
                  htmlFor="mobileNumber"
                >
                  Mobile number
                </label>
                {errors.mobileNumber && (
                  <span className="user-form-sale__field-error error d-block">
                    Mobile number is required
                  </span>
                )}
                {backendErrors.mobileNumber?.map((error, idx) => (
                  <span
                    key={idx}
                    className="user-form-sale__field-error error d-block"
                  >
                    {error}
                  </span>
                ))}
                <div className="autocomplete-container">
                  <input
                    {...register("mobileNumber", { required: true })}
                    className="form-control user-form-sale__input"
                    type="tel"
                    name="mobileNumber"
                    id="mobileNumber"
                    autoComplete="off"
                    inputMode="tel"
                    onClick={(e) => e.stopPropagation()}
                    onFocus={() => setExpandAutocomplete(true)}
                  />
                  <Autocomplete
                    onSelect={handleCustomerSelect}
                    list={customerList}
                    selected={selectedCustomer}
                    expanded={expandAutocomplete}
                    onExpand={setExpandAutocomplete}
                    onClear={handleClearAutocomplete}
                    showCount={true}
                  />
                </div>
              </Col>
              <Col
                xs={12}
                md={6}
                className="mb-1 mb-md-2 user-form-sale__auto-gen-col"
              >
                <Button
                  variant="info"
                  className="user-form-sale__auto-gen"
                  type="button"
                  onClick={handleAutoGenerate}
                >
                  Auto-generate guest
                </Button>
              </Col>
            </Row>

            <Row className="mt-1">
              <Col xs={12}>
                <input
                  type="submit"
                  value={isSubmitting ? "Creating…" : "Create sale"}
                  className="btn btn-primary btn-block user-form-sale__submit"
                  disabled={isSubmitting}
                />
              </Col>
            </Row>
          </form>
          </section>
        </Col>

        <RfidModal
          show={showRfidModal}
          onHide={() => setShowRfidModal(false)}
          onExited={() => {
            setSuccessCustomer(null);
            setErrorMessage(null);
          }}
          successMessage="Customer created successfully"
          successCustomer={successCustomer}
          errorMessage={errorMessage}
          title="Assign RFID"
          hideBalance={true}
          onSubmit={(rfid) => {
            submit(rfid, paymentMethod);
          }}
          showNfcButton={true}
        />
      </Row>
        </div>
        <aside className="user-form-sale__history-rail" aria-label="Recent sales">
          <button
            type="button"
            className="user-form-sale__history-btn"
            onClick={() => setShowSaleHistoryModal(true)}
            title="Last sales (this device)"
          >
            <FontAwesomeIcon icon={faHistory} />
            <span className="user-form-sale__history-btn-text">History</span>
          </button>
        </aside>
      </div>
      <CashOrCreditModal
        show={showMethodModal}
        onHide={() => setShowMethodModal(false)}
        handleChoice={handleMethodChoice}
        disabled={isSubmitting}
        price={
          selectedPurchaseType?.value === "foc"
            ? null
            : (totalCents / 100).toFixed(2)
        }
      />

      <Modal
        show={showSlotDateModal}
        onHide={() => setShowSlotDateModal(false)}
        centered
        className="user-form-sale-date-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Choose visit date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label
            htmlFor="slotVisitDate"
            className="user-form-sale__field-label d-block"
          >
            Date
          </label>
          <input
            id="slotVisitDate"
            type="date"
            className="form-control user-form-sale__input"
            value={pendingSlotDateStr}
            min={formatLocalDate(new Date())}
            onChange={(e) => setPendingSlotDateStr(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSlotDateModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSlotDate}>
            Apply date
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSaleHistoryModal}
        onHide={() => setShowSaleHistoryModal(false)}
        centered
        className="user-form-sale-history-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Recent sales (this device)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {recentSaleHistory.length === 0 ? (
            <p className="user-form-sale-history-modal__empty text-muted mb-0">
              No sales recorded yet. Complete a sale and it will appear here.
            </p>
          ) : (
            <ol className="user-form-sale__history-list">
              {recentSaleHistory.map((sale, i) => (
                <li key={sale.saleId || `${sale.createdAt}-${i}`}>
                  <button
                    type="button"
                    className="user-form-sale__history-line"
                    onClick={() => openCustomerFromHistory(sale)}
                    disabled={isSubmitting}
                  >
                    <span className="user-form-sale__history-line-num">
                      {i + 1}.
                    </span>
                    <span className="user-form-sale__history-line-body">
                      <span className="user-form-sale__history-name">
                        {sale.fullName}
                      </span>
                      <span className="user-form-sale__history-detail">
                        {sale.ticketLine} · {sale.amountLabel} ·{" "}
                        {sale.paymentLabel}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </Modal.Body>
      </Modal>

      <CustomerModal
        show={showActivationModal}
        onHide={closeActivationModal}
        onExited={() => {
          setActivationCustomer(null);
          setActivationError("");
          setActivationIsActivated(null);
          setActivationIsStaff(null);
          setActivationIsCheckedIn(null);
          setActivationCustomerAccess(null);
        }}
        customer={activationCustomer}
        tickets={activationCustomer?.tickets}
        setCustomer={setActivationCustomer}
        customerAccess={activationCustomerAccess}
        isStaff={activationIsStaff}
        isCheckedIn={activationIsCheckedIn}
        setIsCheckedIn={setActivationIsCheckedIn}
        party={party}
        customerLoadError={activationError}
        isActivated={activationIsActivated}
        setIsActivated={setActivationIsActivated}
        showNfcButton={true}
        showActivationButton={!onOpenSaleInPassActivation}
      />
    </React.Fragment>
  );
}
